import {
	ActionButton,
	CommandBar,
	ICommandBarItemProps,
	MessageBar,
	SearchBox,
	Spinner,
	SpinnerSize,
	Stack,
	StackItem,
} from '@fluentui/react';
import { makeAutoObservable } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';
import API, { IEntityV2QueryResultWithType, IGenericEntity } from '../../../API';
import { IUserConfigPackage } from '../../../API';
import DocumentPreviewHTML, {
	DocumentPreviewHTMLState,
} from './DocumentPreviewHTML';

export interface IAttachmentViewerProps {
	state: DocumentViewerState;
}

class DocumentViewer extends React.Component<IAttachmentViewerProps> {
	public componentDidMount(): void {
		this.props.state.loadPreview();
		this.props.state.loadMatchingEntities();
	}

	render() {
		let documentCommandBarItems: ICommandBarItemProps[] = [];

		let isLoadingAIModel = this.props.state.isLoadingAIModel;
		let isUploading = this.props.state.isUploading;
		let previewLoading = this.props.state.documentPreviewState.loading;
		let questionText = this.props.state.aiQuestionText;

		let disableControls = isLoadingAIModel || isUploading || previewLoading;

		if (this.props.state.loadEntityFields) {
			documentCommandBarItems.push({
				key: 'analyzeButton',
				commandBarButtonAs: () => {
					return (
						<ActionButton
							key={'analyze-' + this.props.state.isLoadingAIModel}
							onClick={() => {
								this.props.state.loadAIModel();
							}}
							title={
								"Analyse document with AI, and complete missing fields. Existing form values won't be overwritten."
							}
							disabled={disableControls}
						>
							{isLoadingAIModel ? (
								<Spinner size={SpinnerSize.small} style={{ marginRight: 3 }}></Spinner>
							) : (
								<span>✨</span>
							)}{' '}
							Fill form
						</ActionButton>
					);
				},
			});
		}

		documentCommandBarItems.push({
			key: 'questionInput',
			commandBarButtonAs: () => {
				return (
					<SearchBox
						placeholder="Ask a question..."
						className={'commandBarSearchBox'}
						disableAnimation={true}
						showIcon={true}
						underlined={true}
						disabled={disableControls}
						defaultValue={questionText}
						onChange={(_, newValue) => {
							this.props.state.setAIQuestionText(newValue || '');
						}}
						onSearch={() => {
							this.props.state.sendAIQuestion();
						}}
					/>
				);
			},
		});

		let commandBarFarItems: ICommandBarItemProps[] = [];
		/*{
			key: 'backButton',
			text: 'Previous',
			iconProps: { iconName: 'Back' },
			title: 'Go to previous page',
			disabled: this.props.state.documentPreviewState.currentPage === 1 || disableControls,
			onClick: () => { this.props.state.documentPreviewState.pageBack(); }
		},
		{
			key: 'pageNumber',
			commandBarButtonAs: () => {
				return this.props.state.documentPreviewState.numPages ? <Text style={{margin:12}}>Page {this.props.state.documentPreviewState.currentPage} of {this.props.state.documentPreviewState.numPages}</Text> : <></>
			}
		},
		{
			key: 'nextButton',
			text: 'Next',
			iconProps: { iconName: 'Forward' },
			title: 'Go to next page',
			disabled: this.props.state.documentPreviewState.currentPage === this.props.state.documentPreviewState.numPages || disableControls,
			onClick: () => { this.props.state.documentPreviewState.pageNext(); }
		},*/

		if(this.props.state.isLoadingMatchingEntities) {
			commandBarFarItems.push({
				key: 'openDocButton',
				title: 'Loading matching locations...',
				text: 'Searching for locations...',
				onRenderIcon: () => {
					return <Spinner size={SpinnerSize.xSmall} />
				}
			});
		} else {
			
			this.props.state.matchingEntities.forEach(typeGroup => {
				var entity = typeGroup.entities.items[0];
				if(entity) {
					commandBarFarItems.push({
						key: 'openDocButton',
						iconProps: { iconName: 'TextField' },
						title: 'Edit properties for ' + entity.DisplayTitle,
						text: entity.DisplayTitle,
						disabled: disableControls,
						onClick: () => {

							let w = Math.min(window.screen.width, 900);
							let h = Math.min(window.screen.height, 900);
		
							var x = (window.screen.width/2)-(w/2);
							var y = (window.screen.height/2)-(h/2);

							let url = entity.NativeMetadataUrl + "&helmUX=1";

							let handle = window.open(url, 'helmPopup', `toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=no, resizable=no, copyhistory=no, width=${w}, height=${h}, top=${y}, left=${x}`);
		
							if(!handle){
								window.open(url, '_blank');
							}
						},
					});
				}
			});
		}


		if (this.props.state.onDelete) {
			commandBarFarItems.push({
				key: 'deleteButton',
				iconProps: { iconName: 'Delete' },
				title: 'Delete',
				disabled: disableControls,
				onClick: () => {
					this.props.state.delete();
				},
			});
		}

		commandBarFarItems.push({
			key: 'openDocButton',
			iconProps: { iconName: 'SharePointLogo' },
			title: 'Open file location in SharePoint',
			text: 'Browse files',
			disabled: disableControls,
			onClick: () => {
				this.props.state.openLocation();
			},
		});

		commandBarFarItems.push({
			key: 'openDocButton',
			iconProps: { iconName: 'Edit' },
			title: 'Open for Editing',
			disabled: disableControls,
			onClick: () => {
				this.props.state.openForEditing();
			},
		});

		commandBarFarItems.push({
			key: 'downloadButton',
			iconProps: { iconName: 'Download' },
			title: 'Download',
			disabled: disableControls,
			onClick: () => {
				this.props.state.download();
			},
		});

		commandBarFarItems.push({
			key: 'fullscreenButton',
			iconProps: { iconName: 'Fullscreen' },
			title: 'Full screen',
			disabled: disableControls,
			onClick: () => {
				this.props.state.fullscreen();
			},
		});

		return (
			<>
				<a
					href={this.props.state.downloadLink}
					ref={this.props.state.downloadLinkRef}
					style={{ display: 'none' }}
				>
					&nbsp;
				</a>

				{this.props.state.deleted && (
					<MessageBar>
						This document will be deleted when the form is saved.
					</MessageBar>
				)}

				<div
					style={{
						visibility: this.props.state.deleted ? 'hidden' : 'visible',
					}}
					ref={this.props.state.documentRef}
				>
					<input
						title="file upload hidden element"
						ref={this.props.state.fileUploadRef}
						style={{ display: 'none' }}
						type="file"
						onChange={() => {
							this.props.state.uploadFile();
						}}
					/>

					{this.props.state.isUploading ? (
						<Spinner label="Uploading..." labelPosition="right" />
					) : (
						<></>
					)}

					{this.props.state.fileId && (
						<StackItem>
							<Stack>
								<CommandBar
									onReduceData={() =>
										undefined
									} /* prevents overflow of menu items (the question textfield)*/
									items={documentCommandBarItems}
									farItems={commandBarFarItems}
								></CommandBar>

								<div style={{ position: 'relative' }}>
									{this.getAIAnswerMessage()}
									<DocumentPreviewHTML state={this.props.state.documentPreviewState} />
								</div>
							</Stack>
						</StackItem>
					)}
				</div>
			</>
		);
	}

	private getAIAnswerMessage(): React.ReactNode {
		if (this.props.state.isLoadingAIAnswer || this.props.state.aiAnswerText) {
			return (
				<div
					style={{
						position: 'absolute',
						zIndex: 1,
						width: 'calc(100% - 25px)',
					}}
				>
					<MessageBar messageBarIconProps={{ iconName: 'OfficeChat' }}>
						{this.props.state.isLoadingAIAnswer ? (
							<Spinner size={SpinnerSize.small}></Spinner>
						) : (
							<></>
						)}
						{this.props.state.aiAnswerText}
					</MessageBar>
				</div>
			);
		}

		return <></>;
	}
}

export default observer(DocumentViewer);

export class DocumentViewerState {
	public fileUploadRef = React.createRef<HTMLInputElement>();

	public documentPreviewState: DocumentPreviewHTMLState;
	loadEntityFields?: (entity: IGenericEntity) => void;

	public fileLocation: string = '';
	public fileId: string = '';
	public fileName: string = '';
	onDelete?: () => void;
	public deleted: boolean = false;

	public documentRef = React.createRef<HTMLDivElement>();
	public downloadLinkRef = React.createRef<HTMLAnchorElement>();
	userConfig: IUserConfigPackage;

	public matchingEntities:IEntityV2QueryResultWithType[] = [];
	
	constructor(
		userConfig: IUserConfigPackage,
		loadEntityFields?: (entity: IGenericEntity) => void,
		onDelete?: () => void,
	) {
		this.onDelete = onDelete;
		this.loadEntityFields = loadEntityFields;
		this.userConfig = userConfig;
		this.documentPreviewState = new DocumentPreviewHTMLState(userConfig);

		makeAutoObservable(this);
	}

	public fullscreen() {
		this.documentRef.current?.requestFullscreen();
		this.documentPreviewState.setIsFullscreen(true);
	}

	public downloadLink: string = '';
	public setDownloadLink(val: string) {
		this.downloadLink = val;
	}

	public async download(): Promise<void> {
		let api = new API(await this.userConfig.getToken());
		let downloadLink = await api.prepareDownloadLink(
			this.documentPreviewState.fileIdOrUrl,
		);
		this.setDownloadLink(downloadLink);
		this.downloadLinkRef.current?.click();
	}

	public async openLocation(): Promise<void> {
		let api = new API(await this.userConfig.getToken());
		let editLink = await api.getLocationLink(
			this.documentPreviewState.fileIdOrUrl,
		);
		window.open(editLink, '_blank');
	}

	public async openForEditing(): Promise<void> {
		let api = new API(await this.userConfig.getToken());
		let editLink = await api.getEditLink(this.documentPreviewState.fileIdOrUrl);
		window.open(editLink, '_blank');
	}

	public delete() {
		if (this.onDelete) {
			this.deleted = true;
			this.onDelete();
		}
	}

	public selectUploadDocument() {
		this.fileUploadRef.current?.click();
	}

	public async setFromAttachment(
		id: string,
		url: string,
		name: string,
	): Promise<void> {
		this.fileId = id;
		this.fileLocation = url;
		this.fileName = name;
	}

	public isLoadingMatchingEntities:boolean = false;
	public setIsLoadingMatchingEntities(v:boolean) {
		this.isLoadingMatchingEntities = v;
	}

	public async loadMatchingEntities() {
		this.setIsLoadingMatchingEntities(true);
		var api = new API(await this.userConfig.getToken());
		var matchingEntities = await api.entitySearchByPath(this.fileLocation);
		this.setMatchingEntities(matchingEntities);
		this.setIsLoadingMatchingEntities(false);
	}

	public setMatchingEntities(matchingEntities: IEntityV2QueryResultWithType[]) {
		this.matchingEntities = matchingEntities;
	}

	public async loadPreview() {
		await this.documentPreviewState.setFileIdOrUrl(
			this.fileLocation || this.fileId,
		);
	}

	public aiQuestionText: string = '';
	public setAIQuestionText(arg0: string) {
		this.aiQuestionText = arg0;
	}

	public aiAnswerText: string = '';
	public setAIAnswerText(arg0: string) {
		this.aiAnswerText = arg0;
	}

	public isLoadingAIModel: boolean = false;
	public setIsLoadingAIModel(loading: boolean) {
		this.isLoadingAIModel = loading;
	}

	public isLoadingAIAnswer: boolean = false;
	public setIsLoadingAIAnswer(loading: boolean) {
		this.isLoadingAIAnswer = loading;
	}

	public isUploading: boolean = false;
	public setIsUploading(loading: boolean) {
		this.isUploading = loading;
	}

	public async loadAIModel(): Promise<void> {
		if (this.loadEntityFields) {
			this.setIsLoadingAIModel(true);
			//var api = new API(await this.userConfig.getToken());
			//var aiModel = await api.ai(this.documentPreviewState.fileIdOrUrl);

			//todo - might want to merge?
			//this.loadEntityFields(aiModel);
			this.setIsLoadingAIModel(false);
		}
	}

	public async sendAIQuestion() {
		if (this.aiQuestionText) {
			this.setAIAnswerText('');
			this.setIsLoadingAIAnswer(true);
			var api = new API(await this.userConfig.getToken());
			var answer = await api.question(
				this.documentPreviewState.fileIdOrUrl,
				this.aiQuestionText,
			);
			this.setIsLoadingAIAnswer(false);

			let i = 0;
			this.setAIAnswerText('');
			let words = answer.split(' ');
			let int = setInterval(() => {
				if (i >= words.length) {
					clearInterval(int);
				} else {
					//Add the next char to the answer text
					let substr = words.slice(0, i + 1).join(' ');

					if (i < words.length - 1) {
						substr += '...';
					}

					this.setAIAnswerText(substr);
					i++;
				}
			});
		}
	}

	public async uploadFile(): Promise<void> {
		if (this.fileUploadRef.current?.files) {
			var file = this.fileUploadRef.current.files[0];
			if (file) {
				this.setIsUploading(true);
				//var api = new API(await this.userConfig.getToken());
				//var tempFile = await api.upload(file);

				//this.setFromAttachment(tempFile.location, '', '');

				this.setIsUploading(false);
			}
		}
	}
}
